import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Save, Loader2, Layers, Package, Clock, Rocket, BookOpen, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { SequenceList } from "../components/lms/SequenceList";
import { ScormDropzone } from "../components/lms/ScormDropzone";
import { ScormPlayer } from "../components/lms/ScormPlayer";

/** /admin/formation/modules/:module_id — éditeur plein écran d'un module de cours (compilation de séquences SCORM). */
export default function CourseModuleEditorPage() {
  const { module_id } = useParams();
  const navigate = useNavigate();
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [mod, setMod] = useState(null);
  const [programs, setPrograms] = useState([]);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = useCallback(async () => {
    try {
      const [m, p] = await Promise.all([api.get(`/lms/lessons/${module_id}`), api.get("/lms/programs").catch(() => ({ data: [] }))]);
      setMod(m.data);
      setPrograms((p.data || []).filter((x) => (x.linked_lesson_ids || []).includes(module_id)));
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  // eslint-disable-next-line
  }, [module_id]);
  useEffect(() => { load(); }, [load]);

  const patch = (p) => { setMod((m) => ({ ...m, ...p })); setDirty(true); };

  const save = async (extra = {}) => {
    if (!mod.title?.trim()) return toast.error("Titre requis");
    setSaving(true);
    try {
      const body = { title: mod.title, description: mod.description || "", status: mod.status, duration_min: mod.duration_min || 0, module_ids: mod.module_ids, ...extra };
      const r = await api.put(`/lms/lessons/${module_id}`, body);
      setMod(r.data); setDirty(false);
      toast.success(extra.status === "published" ? "Module publié — disponible pour vos programmes" : "Module enregistré");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    setSaving(false);
  };

  const reorder = (ids) => patch({ module_ids: ids, modules: ids.map((id) => mod.modules.find((s) => s.module_id === id)).filter(Boolean) });

  const renameSeq = async (mid, title) => {
    try { await api.put(`/lms/course-modules/${mid}`, { title }); setMod((m) => ({ ...m, modules: m.modules.map((s) => (s.module_id === mid ? { ...s, title } : s)) })); toast.success("Séquence renommée"); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const durationSeq = async (mid, duration_min) => {
    setMod((m) => ({ ...m, modules: m.modules.map((s) => (s.module_id === mid ? { ...s, duration_min } : s)) }));
    try { await api.put(`/lms/course-modules/${mid}`, { duration_min }); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  const removeSeq = async (s) => {
    if (!window.confirm(`Retirer la séquence « ${s.title} » ? Le package SCORM sera supprimé s'il n'est utilisé nulle part ailleurs.`)) return;
    try { const r = await api.delete(`/lms/lessons/${module_id}/modules/${s.module_id}`); setMod((m) => ({ ...m, ...r.data })); toast.success("Séquence retirée"); } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  if (!mod) return <div className="p-20 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto text-[#f7c935]" /></div>;
  const seqs = mod.modules || [];
  const total = seqs.reduce((a, s) => a + (s.duration_min || 0), 0);

  return (
    <div className="min-h-screen" data-testid="course-module-editor">
      <div className="sticky top-0 z-20 bg-[#0b0811]/95 backdrop-blur border-b border-white/10 px-5 py-3 flex flex-wrap items-center gap-3">
        <Button variant="ghost" onClick={() => navigate("/admin/formation/modules")} className="text-slate-400" data-testid="cm-back"><ArrowLeft className="w-4 h-4 mr-1" /> Modules</Button>
        <Layers className="w-4 h-4 text-[#f7c935] shrink-0" />
        <input value={mod.title || ""} onChange={(e) => patch({ title: e.target.value })} placeholder="Titre du module de cours" className="flex-1 min-w-[200px] bg-transparent border-0 outline-none text-xl font-black text-white placeholder:text-slate-600" data-testid="cm-title-input" />
        <span className={"text-[9px] px-2 py-1 rounded uppercase font-bold " + (mod.status === "published" ? "bg-green-500/15 text-green-400" : "bg-amber-500/15 text-amber-400")} data-testid="cm-status-badge">{mod.status === "published" ? "Publié" : "Brouillon"}</span>
        {dirty && <span className="text-[10px] text-amber-300" data-testid="cm-dirty">Modifications non enregistrées</span>}
        <Button onClick={() => save()} disabled={saving} variant="outline" className="border-white/15 text-white font-bold" data-testid="cm-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Enregistrer</Button>
        {mod.status === "published" ? (
          <Button onClick={() => save({ status: "draft" })} disabled={saving} variant="ghost" className="text-slate-300" data-testid="cm-unpublish-btn"><Undo2 className="w-4 h-4 mr-1" /> Repasser en brouillon</Button>
        ) : (
          <Button onClick={() => (seqs.length ? save({ status: "published" }) : toast.error("Ajoutez au moins une séquence SCORM avant de publier"))} disabled={saving} className="bg-[#f7c935] text-[#14101c] font-bold" data-testid="cm-publish-btn"><Rocket className="w-4 h-4 mr-1" /> Compiler & publier</Button>
        )}
      </div>

      <div className="grid grid-cols-12 gap-4 p-4">
        <main className="col-span-12 lg:col-span-8 space-y-3">
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-4">
            <div className="flex items-center justify-between mb-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold flex items-center gap-1"><Package className="w-3 h-3 text-sky-400" /> Séquences SCORM — ordre de lecture ({seqs.length})</div>
              <div className="text-[10px] text-slate-500 inline-flex items-center gap-1" data-testid="cm-total-duration"><Clock className="w-3 h-3" /> {total} min au total</div>
            </div>
            {seqs.length === 0 && <div className="text-xs text-slate-500 italic text-center py-6" data-testid="cm-sequences-empty">Aucune séquence. Déposez vos packages SCORM ci-dessous : chaque package devient une séquence.</div>}
            <SequenceList sequences={seqs} onReorder={reorder} onRename={renameSeq} onDuration={durationSeq} onPreview={setPreview} onRemove={removeSeq} />
            <div className="mt-3"><ScormDropzone api={api} lessonId={module_id} compact={seqs.length > 0} onImported={(d) => { setMod((m) => ({ ...m, ...d.lesson })); setDirty(false); }} /></div>
          </div>
        </main>

        <aside className="col-span-12 lg:col-span-4 space-y-3">
          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-4 space-y-3">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold">Réglages du module</div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Description (visible des stagiaires)</label>
              <Textarea value={mod.description || ""} onChange={(e) => patch({ description: e.target.value })} rows={4} className="bg-[#0b0811] border-white/10 text-white text-xs" data-testid="cm-description" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Durée affichée (min) — vide = somme des séquences ({total} min)</label>
              <Input type="number" min="0" value={mod.duration_min ?? ""} onChange={(e) => patch({ duration_min: e.target.value === "" ? 0 : parseInt(e.target.value, 10) })} className="bg-[#0b0811] border-white/10 text-white" data-testid="cm-duration" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 block mb-1">Statut</label>
              <select value={mod.status || "draft"} onChange={(e) => patch({ status: e.target.value })} className="w-full bg-[#0b0811] border border-white/10 rounded px-2 py-1.5 text-xs text-white" data-testid="cm-status-select">
                <option value="draft">Brouillon</option>
                <option value="published">Publié</option>
              </select>
            </div>
          </div>

          <div className="bg-[#14101c] border border-white/10 rounded-2xl p-4" data-testid="cm-programs">
            <div className="text-[10px] uppercase tracking-wider text-slate-400 font-bold mb-2 flex items-center gap-1"><BookOpen className="w-3 h-3 text-[#f7c935]" /> Programmes utilisant ce module ({programs.length})</div>
            {programs.length === 0 ? (
              <div className="text-xs text-slate-500 italic">Aucun. Rattachez-le depuis un programme (Programmes de formations → Rattachements → Modules de cours).</div>
            ) : (
              <ul className="space-y-1">{programs.map((p) => <li key={p.program_id}><button type="button" onClick={() => navigate(`/admin/formation/bibliotheque/${p.program_id}/edit`)} className="text-xs text-sky-300 hover:underline truncate block max-w-full text-left" data-testid={`cm-program-${p.program_id}`}>{p.title}</button></li>)}</ul>
            )}
          </div>
        </aside>
      </div>

      {preview && (
        <Dialog open onOpenChange={(o) => { if (!o) setPreview(null); }}>
          <DialogContent className="max-w-5xl bg-[#0b0811] border border-white/10 text-white" data-testid="cm-preview-dialog">
            <DialogHeader><DialogTitle className="flex items-center gap-2 text-sm"><Package className="w-4 h-4 text-sky-400" /> Test de la séquence — {preview.title} <span className="text-[10px] text-slate-500 font-normal">(sans enregistrement de progression)</span></DialogTitle></DialogHeader>
            <ScormPlayer sequence={{ sequence_id: preview.module_id, scorm_entry: preview.scorm_entry, scorm_version: preview.scorm_version, title: preview.title }} enrollmentId={null} api={api} />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
